// frontend/src/marketing/pages/legal/DpaPage.tsx
import * as React from 'react';
import { LegalPageLayout, type LegalSection } from './LegalPageLayout';

const SECTIONS: LegalSection[] = [
  {
    heading: 'Roles',
    body: (
      <p>
        For Customer Data inside a workspace, the customer (the organization that owns the
        workspace) is the data controller and Worqly is the data processor. This Data Processing
        Addendum ("DPA") forms part of the{' '}
        <a className="underline underline-offset-2" href="/legal/terms">Terms of Service</a> for all
        customers whose use of the Service involves personal data subject to data-protection law
        (including GDPR).
      </p>
    ),
  },
  {
    heading: 'Processing instructions',
    body: (
      <p>
        We process Customer Data only on the customer's documented instructions — i.e., to provide
        the modules the customer has enabled — and never for our own purposes. Personnel with
        access are bound by confidentiality.
      </p>
    ),
  },
  {
    heading: 'Categories of data and data subjects',
    body: (
      <p>
        Data subjects: the customer's users, employees, clients and contacts. Categories: contact
        details, employment records (role, salary components, attendance, leave), financial records
        (invoices, expenses, payouts), time-tracking entries, and — where the monitoring module is
        enabled — workstation screenshots. The customer is responsible for lawful basis, including
        any notice or consent local law requires for employee monitoring.
      </p>
    ),
  },
  {
    heading: 'Sub-processors',
    body: (
      <p>
        The customer authorizes the sub-processors listed in the{' '}
        <a className="underline underline-offset-2" href="/legal/privacy">Privacy Policy</a> (hosting,
        payments, email, bot protection, rate limiting, and — where the customer connects it —
        accounting sync). We will give at least 14 days' notice before adding or
        replacing a sub-processor, during which the customer may object on reasonable grounds.
      </p>
    ),
  },
  {
    heading: 'Security measures',
    body: (
      <p>
        TLS in transit, encryption at rest, per-workspace row-level isolation, role-based access
        control, hashed credentials, signed webhooks, audit logging, and least-privilege operator
        access. Details and roadmap on the{' '}
        <a className="underline underline-offset-2" href="/security">security page</a>.
      </p>
    ),
  },
  {
    heading: 'Data-subject requests and assistance',
    body: (
      <p>
        We forward any data-subject request we receive directly to the customer and provide
        reasonable assistance (export, correction, deletion tooling) so the customer can meet its
        obligations, including assistance with security-incident notification without undue delay
        after becoming aware of a personal-data breach affecting Customer Data.
      </p>
    ),
  },
  {
    heading: 'Deletion and return',
    body: (
      <p>
        On termination, Customer Data is retained for a 30-day grace window for recovery and then
        purged from production systems; rolling database backups may retain it for up to a further
        14 days before they rotate out. During the grace window the customer may request an export.
      </p>
    ),
  },
  {
    heading: 'Transfers',
    body: (
      <p>
        Where processing involves transfers out of the EEA/UK, the parties rely on the European
        Commission's Standard Contractual Clauses as incorporated by our sub-processors'
        agreements.
      </p>
    ),
  },
];

export function DpaPage() {
  return (
    <LegalPageLayout
      eyebrow="Legal"
      title="Data Processing Addendum"
      description="Worqly's processor commitments for customer workspace data."
      updated="June 4, 2026"
      sections={SECTIONS}
    />
  );
}
